import React from 'react';
import { 
  ComboBox as KendoComboBox, 
  ComboBoxFilterChangeEvent, 
  ListItemProps
} from '@progress/kendo-react-dropdowns';
import { PureComponent } from '../../../../dispatcher/modules';
import * as _ from 'lodash';
import { classNames } from '@progress/kendo-react-common';
import PuzzleButton from '../../button/PuzzleButton';
import { ACTION_BTN_SETTINGS } from '../Types';
import { PUZZLE_COMBO_BOX_PROPS, PUZZLE_COMBO_BOX_STATE } from './Types';
import Field from '../../../layout/field/Field';
import { Label } from '@progress/kendo-react-labels';
import { 
  ABSTRACT_FIELD_OMITTED_PROPS, 
  FIELD_OMITTED_PROPS
} from '../../../../utility';
import PuzzleTooltip from '../../tooltip/PuzzleTooltip';
import WithTooltip from '../../../../utility/withTooltip';
import { filterBy, FilterDescriptor } from '@progress/kendo-data-query';
import { Skeleton } from '@progress/kendo-react-indicators';

/**
 * For the layout and design purposes the entire Kendo Combobox needs to be wrapped with this cmp.
 * Cmp handles all events that occur and flags component state represented with CSS classes, cmp also can be used with
 * additional action btn
 * @author Stevan Stojanovic
 */
class PuzzleComboBox extends PureComponent<PUZZLE_COMBO_BOX_PROPS, PUZZLE_COMBO_BOX_STATE> {
  comboBoxRef: any = null;
  isOpen = false;
  tooltip: any = null;
  comboBoxID: any = null;
  /**
   * Filter value. It uses for remembering filter value because `onFilterChange` listener can be changed.
   */
  filterValue: string;

  state: PUZZLE_COMBO_BOX_STATE = {
    filter: {},
    toolTipValue: null,
    toolTipText: '',
    isLastPage: false
  }

  static defaultProps = {
    size: 'medium'
  }

  componentDidMount() {
    this.comboBoxID = this.props.id ? this.props.id : this.comboBoxRef?._input?.id;
    this.setTabIndexOnInput();
  }

  componentDidUpdate(prevProps: Readonly<PUZZLE_COMBO_BOX_PROPS>, prevState: Readonly<PUZZLE_COMBO_BOX_STATE>) {
    const { value, showTooltip = true } = this.props;
    const { toolTipValue, toolTipText, isLastPage } = this.state;

    // If there is a need to change the text in the tooltip
    if (showTooltip && _.isUndefined(value) && prevState.toolTipValue !== toolTipValue) {
      if (!this.isOpen && this.isEllipsisActive(this.comboBoxRef?._input)) {
        this.setState({ toolTipText: toolTipValue });
      } else if (toolTipText !== '') {
        this.setState({ toolTipText: '' });
      }
    }

    if (isLastPage) {
      // if is the last page in the virtual scroll, then the component will be rerendered
      this.setState({ isLastPage: false });
    }

    this.setTabIndexOnInput();
  }

  private assignComboBoxRef = (comboBoxRef: KendoComboBox) => {
    this.comboBoxRef = comboBoxRef;
  };

  private assignTooltipRef = (tooltip: PuzzleTooltip) => {
    this.tooltip = tooltip;
  };

  /**
   * Function for setting tabindex on input element by locked state
   */
  private setTabIndexOnInput = (): void => {
    if (this.props.locked) {
      this.comboBoxRef?._input.setAttribute('tabindex', -1);
    } else {
      this.comboBoxRef?._input.removeAttribute('tabindex');
    }
  }

  /**
   * Action button click handler
   */
  private actionBtnOnClickHandler = (buttonType: string, e: React.MouseEvent<HTMLButtonElement>) => {
    const itemByType: ACTION_BTN_SETTINGS | undefined = _.find(this.props.actionBtnSettings, {
      buttonType
    });
    itemByType?.onClick && itemByType.onClick(e);
    this.comboBoxRef?.focus();
  }

  /**
   * Action button render
   * */
  private actionBtnRender = () => {
    const { actionBtnSettings } = this.props;

    return actionBtnSettings?.map((button: ACTION_BTN_SETTINGS, index: number) => {
      const buttonActualProps = _.omit(
        button,
        ['buttonType', 'ariaLabel']
      );

      return (
        <PuzzleButton
          {...buttonActualProps}
          key={index}
          aria-label={button.ariaLabel}
          onClick={this.actionBtnOnClickHandler.bind(this, button.buttonType)}
        />
      );
    });
  };

  /**
   * Item render for combobox with correct id name which will reflect id from the drop-down(a11y manner)
   * aria-activedescendant refers to the same id from the dropdown but without 'option-' string
   */
  private itemRender = (li: React.ReactElement<HTMLLIElement>, itemProps: ListItemProps) => {
    let {
      dataItem
    } = itemProps;

    let text: any = dataItem;
    if (dataItem && this.props.textField) {
      text = dataItem[this.props.textField];
    }
    const modifiedProps = {
      ...li.props,
      id: itemProps.id?.replace('option-', ''),
      className: `${li.props?.className} ${dataItem && dataItem.itemDisabled ? 'k-disabled tp-pointer-events-none' : ''}`
    };

    if (!text && this.props.skeletonEnabled) {
      text = (
        <Skeleton
          shape={'text'}
          style={{ width: '100%' }}
          animation={{ type: 'wave' }}
        />);
    } else {
      text = (
        <span
          className={'k-list-item-text ' + (dataItem && dataItem.itemDisabled ? 'k-disabled tp-pointer-events-none' : '')}
          key={'data-1'}
          title={text}
        >{text}</span>
      );
    }

    // add span with classname
    return React.cloneElement(li, modifiedProps, [text]);
  };

  private isEllipsisActive = (input: HTMLInputElement) => (input && (input.offsetWidth < input.scrollWidth));

  private onClose = (e) => {
    this.filterValue = '';
    if (!e.value || !e.value.itemDisabled) {
      this.isOpen = false;
      this.props.onClose?.(e);
    }
  };

  private onOpen = (event) => {
    this.isOpen = true;
    this.props.onOpen?.(event);

    const virtualScroll = this.comboBoxRef?.base?.vs;
    if (!!virtualScroll) {
      const { pageSize, skip, total } = virtualScroll;
      if (skip + pageSize === total) {
        this.setState({ isLastPage: true });
      }
    }
  };

  valueRender = (defaultRender: any) => {
    const { showTooltip = true, errorMessageToolTip, errorTxt = '', valid } = this.props;

    if (showTooltip || errorMessageToolTip) {
      const isValid = !(valid === false && _.isEmpty(this.filterValue));
      const title = errorMessageToolTip ? (isValid ? '' : errorTxt) : !this.isOpen && this.isEllipsisActive(this.comboBoxRef?._input) ? defaultRender.props.value : '';

      const tooltipCls = classNames(
        'tp-puzzle-tooltip', {
          '!k-display-none': title === ''
        }
      );

      return (
        <>
          <WithTooltip
            context={this}
            htmlTag="span"
            title={title}
          >
            {React.cloneElement(defaultRender, { title })}
          </WithTooltip>
          <PuzzleTooltip
            anchorElement="target"
            openDelay={500}
            position="top"
            ref={this.assignTooltipRef}
            tooltipClassName={tooltipCls}
          />
        </>
      );
    } else {
      return defaultRender;
    }
  };

  onChange = (e: any) => {
    const { value, showTooltip = true } = this.props;
    if (e.value?.itemDisabled) {
      const keyCode = e.syntheticEvent.keyCode;
      let next;
      let data = _.get(e, 'target.props.data');
      const index = data.indexOf(e.value);
      if (keyCode === 40) { // Arrow Down
        next = data.slice(index).find(i => !i.itemDisabled);
      } else if (keyCode === 38) { // Arrow Up
        data = data.slice(0, index);
        next = data.pop();
        while (next && next.itemDisabled) {
          next = data.pop();
        }
      }

      if (next) {
        e.value = next;
      }
    }

    // propagate to next prototype in line
    this.props.onChange?.(e);

    // If there is no prop value and showTooltip is true, a new rerender must be triggered to check in componentDidUpdate if there is a need to change the text in the tooltip.
    if (showTooltip && _.isUndefined(value)) {
      this.setState({
        toolTipValue: e.value
      });
    }
  }

  /**
   * Fires each time the user types in the filter input. You can filter the source based on the passed filtration value.
   * @param {ComboBoxFilterChangeEvent} event
   */
  private filterChange = (event: ComboBoxFilterChangeEvent) => {
    this.filterValue = event.filter.value;
    if (this.props.onFilterChange) {
      this.props.onFilterChange(event);
    } else {
      this.setState({
        filter: event.filter
      });
    }
  };

  /**
   * Filters data and returns new filtered data
   * @param {string[]} data
   * @param {FilterDescriptor} filter
   */
  private filterData(data: string[], filter: FilterDescriptor):string[] {
    return filterBy(data, filter);
  }

  render(): any {
    const {
      asField,
      actionBtnSettings,
      ariaDescribedBy,
      dataItemKey,
      disabled,
      errorTxt,
      className = '',
      label,
      labelClassName = '',
      locked = false,
      value,
      valid,
      size,
      withValidation,
      fieldClassName = '',
      fieldMandatory,
      fieldWithAdditionalEl,
      dataCy,
      filterable = true
    } = this.props;

    let { data, omitItems = [] } = this.props;

    const {
      filter
    } = this.state;

    const asFieldProps = {
      fieldClassName,
      fieldMandatory,
      fieldWithAdditionalEl
    };

    const comboboxOmittedProps = ['actionBtnSettings', 'withAction'].concat(FIELD_OMITTED_PROPS, ABSTRACT_FIELD_OMITTED_PROPS);
    const comboBoxProps = _.omit(this.props, comboboxOmittedProps);

    if (filterable && !_.isEmpty(filter)) {
      if (_.isFunction(this.props.filterData)) {
        data = this.props.filterData(data, filter);
      } else {
        data = this.filterData(data, filter);
      }
    }
    // Commented due inconsistent behavior of disabling or not showing disabled, omitted items on combobox. It has been done on request of TMA-7869.
    // When we have two or more combo boxes that use same data, if one record is selected by one it will not be shown in others.
    // Code below is used if records in case mentioned above is not hide but disabled.
    // if there are any disabled items mark them in data array or unmark them if they are enabled in some moment
    // if (data) {
    //   data = [...data];
    //   data.map((item: any) => {
    //     const itemValue = dataItemKey ? item[dataItemKey] : item;
    //     item.itemDisabled = omitItems.indexOf(itemValue) !== -1;
    //   });
    // }

    // Currently, behavior is to hide, not to show omitted items.
    // removes omitItems from combobox picker if there are two or more that use the same data
    if (omitItems && omitItems.length) {
      data = data?.filter(item => !omitItems.includes(item[dataItemKey]));
    }

    const isValid = !(valid === false && _.isEmpty(this.filterValue));

    const fieldCls = classNames(
      {
        [className]: className,
        'tp-combobox-wrap': true,
        'tp-with-action': !_.isEmpty(actionBtnSettings),
        'k-medium': size === 'medium',
        'k-small': size === 'small',
        'k-state-empty': !value,
        'k-state-invalid': !isValid,
        'k-state-disabled': disabled,
        'k-state-locked': locked
      }
    );

    const content = (
      <div
        data-cy={dataCy ? `${dataCy}-wrapper` : undefined}
        className={fieldCls}
      >
        <div className={'tp-combobox'}>
          {label && <Label
            editorId={this.comboBoxID}
            className={classNames({
              [labelClassName]: labelClassName,
              'k-mandatory': !asField && fieldMandatory
            })}
          >
            {label}
          </Label>}
          <KendoComboBox
            id={this.comboBoxID}
            ref={this.assignComboBoxRef}
            data-cy={dataCy ? dataCy : undefined}
            popupSettings={{
              popupClass: 'data-preventrecorddeselect'
            }}
            {...comboBoxProps}
            onFilterChange={this.filterChange}
            filterable={filterable}
            itemRender={this.itemRender}
            onOpen={this.onOpen}
            onClose={this.onClose}
            valueRender={this.valueRender}
            onChange={this.onChange}
            data={data}
          />
          {this.actionBtnRender()}
        </div>
        {withValidation && <div
          className={'tp-validation-error-message'}
          data-cy={dataCy ? `${dataCy}-error-message` : undefined}
          id={ariaDescribedBy}
        >
          {isValid ? '' : errorTxt}
        </div>}
      </div>
    );

    if (asField) {
      return (
        <Field {...asFieldProps}>
          {content}
        </Field>
      );
    }

    return content;
  }

  /**
   * Helper method for triggering events outside the cmp itself
   */
  triggerEvent(eventType: string, event: any) {
    if (this.props[eventType]) {
      this.props[eventType].call(undefined, {
        ...event,
        target: this.comboBoxRef
      });
    }
  }
}

export default PuzzleComboBox;
